import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

// API
import {
  useCopyMachineConfigurationDataMutation,
  useDeleteCopiedWidgetsMutation,
  useDeleteTagsImageMutation,
  useGetMachineConfiguratorDataQuery,
  useLazyGetMachineConfiguratorDataQuery,
  useUploadTagsImageMutation
} from 'api';

// Types
import { TopLevelTabs } from 'types/protein';
import { WidgetTableDataItem } from 'types/machine-health';

// Helpers
import { updateDeeplyNestedIds } from 'common/helpers/v1/machine-health';
import { ToastMsg } from 'common/components/Toast';
import { extractImageData, uploadExtractedImages } from '../utils';

// Providers
import { useLanguage } from 'providers';

interface UseCopyConfigurationOptions {
  tabData: WidgetTableDataItem | undefined;
  setTabData: React.Dispatch<React.SetStateAction<WidgetTableDataItem | undefined>>;
  setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
  /** The MachineHealthTabs label to find the tab from copied data */
  machineHealthTabLabel: string;
  /** The top-level tab label (defaults to MachineHealth) */
  topLevelTabLabel?: string;
  /** Whether this tab supports clearing widgets (shows Clear Configuration + WarningPrompt) */
  supportsClearWidgets?: boolean;
}

export const useCopyConfiguration = ({
  tabData,
  setTabData,
  setIsDirty,
  machineHealthTabLabel,
  topLevelTabLabel = TopLevelTabs.MachineHealth,
  supportsClearWidgets = false
}: UseCopyConfigurationOptions) => {
  const { machineId } = useParams<{ machineId: string }>();
  const { languageId } = useLanguage();

  const [
    copyMachineConfigurationData,
    { isLoading: updatingConfiguration, error: errorUpdateConfiguration }
  ] = useCopyMachineConfigurationDataMutation();

  const [deleteCopiedWidgets] = useDeleteCopiedWidgetsMutation();
  const [deleteTagsImage] = useDeleteTagsImageMutation();
  const [uploadTagsImage] = useUploadTagsImageMutation();
  const [getMachineConfiguratorData] = useLazyGetMachineConfiguratorDataQuery();

  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyMachineId, setCopyMachineId] = useState('');
  const [skip, setSkip] = useState(true);
  const [copiedConfiguration, setCopiedConfiguration] = useState<
    WidgetTableDataItem[] | undefined
  >();
  const [isCopying, setIsCopying] = useState(false);
  const [copyingMachineWidgets, setCopyingMachineWidgets] = useState(false);
  const [isViewConfirmCancel, setIsViewConfirmCancel] = useState(false);

  const {
    data: copyData,
    isFetching: copyFetching,
    error: errorCopy
  } = useGetMachineConfiguratorDataQuery(
    {
      machineId: copyMachineId,
      labels: [],
      languageId: languageId,
      showInactive: true
    },
    { skip }
  );

  const copyConfiguration = async () => {
    if (!copiedConfiguration) {
      return;
    }

    // Extract image data and strip imageUrl/imageSize from configuration
    const { strippedItems, images } = extractImageData(copiedConfiguration);

    await copyMachineConfigurationData({
      machineId,
      configuration: strippedItems,
      languageId: languageId
    })
      .unwrap()
      .then(async () => {
        ToastMsg({
          heading: 'Success',
          type: 'success',
          message: `Configuration copied successfully`
        });
        setIsDirty(false);
        setCopiedConfiguration(undefined);
        setCopyingMachineWidgets(true);

        if (images.length > 0) {
          await uploadExtractedImages(uploadTagsImage, machineId, images);
        }
      })
      .catch((error) => {
        ToastMsg({ heading: 'Error', type: 'error', message: `Failed to copy configuration` });
        console.error(error?.data?.detail);
      });
  };

  const onCopyFromSelectedMachine = (id: string) => {
    setCopyMachineId(id);
    setSkip(false);
    setCopyModalOpen(false);
    if (supportsClearWidgets) {
      setCopyingMachineWidgets(true);
    }
  };

  const onClickDelete = () => {
    setIsViewConfirmCancel(true);
  };

  const onClickDeleteCopiedWidgets = async () => {
    try {
      // Fetch current machine widget data
      const widgetData = await getMachineConfiguratorData({
        machineId,
        labels: [],
        languageId,
        showInactive: true
      }).unwrap();

      // Extract images from widget data and delete them first
      if (widgetData && widgetData.length > 0) {
        const { images } = extractImageData(widgetData);

        if (images.length > 0) {
          const deleteImagePromises = images.map((image) =>
            deleteTagsImage({
              machineId,
              widgetId: image.id
            })
              .unwrap()
              .catch((error) => {
                console.error('Error deleting tag image:', error);
              })
          );
          await Promise.all(deleteImagePromises);
        }
      }

      // Then delete widgets
      await deleteCopiedWidgets({ machineId }).unwrap();

      ToastMsg({ heading: 'Success', type: 'success', message: `Widgets deleted successfully` });
      setIsDirty(false);
      setCopiedConfiguration(undefined);
      setCopyingMachineWidgets(false);
      setIsViewConfirmCancel(false);
    } catch (error) {
      ToastMsg({ heading: 'Error', type: 'error', message: `Failed to delete widgets` });
      console.error(error);
    }
  };

  const openCopyModal = () => {
    setCopyModalOpen(true);
    setIsCopying(true);
  };

  // Handle copy data arrival
  useEffect(() => {
    if ((!copyData || !copyData?.[0]) && isCopying && !copyModalOpen && !copyFetching) {
      ToastMsg({
        heading: 'Error',
        type: 'error',
        message: 'No configuration data found, try another machine'
      });
      setIsCopying(false);
    } else if (copyData && copyData.length > 0 && !tabData) {
      const nextCopiedConfiguration = copyData.map((item) =>
        updateDeeplyNestedIds(item, 'members')
      );

      if (!supportsClearWidgets || copyingMachineWidgets) {
        setTabData(
          nextCopiedConfiguration
            ?.find((item) => item.label === topLevelTabLabel)
            ?.members?.find((member) => member.label === machineHealthTabLabel)
        );
        setCopiedConfiguration(nextCopiedConfiguration);
      }
      setSkip(true);
      setIsDirty(true);
      setIsCopying(false);
    }
  }, [copyData, tabData, isCopying, copyModalOpen, copyFetching]);

  return {
    // State
    copiedConfiguration,
    copyModalOpen,
    copyFetching,
    updatingConfiguration,
    errorCopy,
    errorUpdateConfiguration,
    isViewConfirmCancel,

    // Setters
    setCopyModalOpen,
    setIsViewConfirmCancel,

    // Actions
    copyConfiguration,
    onCopyFromSelectedMachine,
    onClickDelete,
    onClickDeleteCopiedWidgets,
    openCopyModal
  };
};
